<template>
  <v-container
    fluid
    class="submit-design">
    <!-- META-DATA -->
    <vue-headful
      :title="'Submit Your Design' + this.$store.state.app.meta.titleSuffix"
      :description="this.$store.state.app.meta.description" />

    <v-scroll-y-transition
      group
      hide-on-leave
      class="stepper">
      <!-- UPLOAD -->
      <div
        v-if="stepper===0"
        key="upload"
        class="stepper__item">
        <custom-upload
          @set-source-image="onUpload($event)" />
      </div>

      <!-- FORMAT -->
      <div
        v-if="stepper===1"
        key="format"
        class="stepper__item">
        <custom-format
          ref="customFormat"
          :source-image="sourceImageURL"
          @canvas-output="onCanvasOutput($event)"
          @landscape-next="nextStep" />
      </div>

      <!-- EDIT META-DATA -->
      <div
        v-if="stepper===2"
        key="edit-data"
        class="stepper__item">
        <v-container class="edit-data">
          <div class="edit-data__image-column">
            <img
              :src="outputImageURL"
              class="custom-patch-image">
          </div>
          <div class="edit-data__form-column">
            <v-form
              ref="form"
              v-model="submitFormValid"
              class="patch-form">
              <h2 class="patch-form__header">about your design</h2>

              <v-text-field
                ref="name"
                v-model="formData.name"
                label="Name/Title"
                class="patch-form__name"
                :rules="[
                  v => !!v || 'This field is required',
                ]"
                required />

              <v-text-field
                ref="alias"
                v-model="formData.alias"
                label="Alias"
                class="patch-form__alias"
                :rules="[
                  v => !!v || 'This field is required',
                  v => validateAlias(v) || 'This alias is already taken',
                ]"
                validate-on-blur
                required>
                <v-tooltip
                  slot="append-outer"
                  bottom>
                  <v-icon slot="activator">help_outline</v-icon>
                  Your alias is displayed below any patches you submit. Changing your alias here will change it on all of your existing patches.
                </v-tooltip>
              </v-text-field>

              <v-textarea
                ref="description"
                v-model="formData.description"
                label="Description"
                rows="4"
                :rules="[
                  v => !!v || 'This field is required',
                ]"
                class="patch-form__description" />

              <v-combobox
                v-model="formData.suggestedTags"
                :items="sortedTags"
                label="Suggest tags"
                hint="Select tags, or create your own"
                prepend-inner-icon="local_offer"
                :counter="tagLimit"
                multiple
                persistent-hint
                class="tag-select">
                <template
                  slot="selection"
                  slot-scope="{ item, parent }">
                  <div
                    v-ripple
                    class="tag-select__selected-tag"
                    @click="parent.selectItem(item)">
                    {{ item }}
                    <v-icon
                      small
                      class="tag-select__selected-tag-remove">
                      close
                    </v-icon>
                  </div>
                </template>
              </v-combobox>

              <v-checkbox
                v-model="formData.agreeToTerms"
                color="primary"
                :rules="[
                  v => !!v || 'This field is required',
                ]">
                <template v-slot:label>
                  <span class="patch-form__terms">I agree to the <router-link :to="{ name: 'terms-and-conditions' }">Terms and Conditions</router-link> and the <router-link :to="{ name: 'artist-agreement' }">Artist Agreement</router-link>.</span>
                </template>
              </v-checkbox>
            </v-form>
          </div>
        </v-container>
      </div>

      <!-- PREVIEW & SUBMIT -->
      <div
        v-if="stepper===3"
        key="preview"
        class="stepper__item">
        <v-container class="submit-preview">
          <div class="preview">
            <img
              :src="outputImageURL"
              class="custom-patch-image">
            <h2 class="preview__name">
              {{ formData.name }}
            </h2>
            <div class="preview__creator-row">
              <span>
                Created By: {{ formData.alias }}
              </span>
              <span>
                Created On: {{ moment().format('MMM Do, YYYY') }}
              </span>
            </div>
            <v-divider />
            <div
              v-if="formData.description"
              class="preview__description">
              {{ formData.description }}
            </div>
            <v-divider />
            <div class="preview__tags">
              <span
                v-for="tag in formData.suggestedTags"
                :key="tag"
                class="preview__tag">
                {{ tag }}
              </span>
              <span
                v-if="formData.suggestedTags.length===0"
                class="preview__no-tags">
                No Suggested Tags
              </span>
            </div>
          </div>

          <div class="submit-info">
            <h2 class="submit-info__header">all set</h2>
            <p class="submit-info__body-text">
              If everything looks correct, your design is ready to submit!
            </p>
            <p class="submit-info__body-text">
              After clicking 'submit' you will be taken to your patch's listing page; it is important that you save the URL for your patch's page somewhere, as that URL will be the only way to access your patch.
            </p>
            <v-divider />
            <div class="submit-button__wrapper">
              <v-btn
                class="submit-button"
                color="primary"
                @click="submitDesign">
                submit my design
              </v-btn>
            </div>
          </div>
        </v-container>
      </div>

      <!-- PROCESSING -->
      <div
        v-if="stepper===4"
        key="processing"
        class="stepper__item">
        <v-scale-transition
          group
          hide-on-leave>
          <!-- processing -->
          <v-container
            v-if="!submitSuccess && !submitFail"
            key="processing"
            class="processing">
            <h3
              class="processing__header">
              submitting...
            </h3>
            <v-progress-circular
              indeterminate
              size="100"
              width="7"
              value="true"
              class="processing__loader" />
          </v-container>
          <!-- success -->
          <v-container
            v-if="submitSuccess"
            key="success"
            class="processing">
            <h3
              class="processing__header">
              success
            </h3>
            <v-icon class="processing__icon">check_circle</v-icon>
            <v-fade-transition>
              <span
                v-if="redirectCountdown !== 0"
                class="processing__redirect-countdown">
                redirecting in {{ redirectCountdown }}
              </span>
            </v-fade-transition>
          </v-container>
          <!-- fail -->
          <v-container
            v-if="submitFail"
            key="fail"
            class="processing">
            <h3
              class="processing__header">
              Submission Failed
            </h3>
            <v-icon class="processing__icon">error</v-icon>
            <span>A server error occurred and your patch was not able to be submitted - please try again later.</span>
            <br>
            <span>This feature is currently in beta testing, which means we are still working out the kinks. Consider submitting a bug report: reported bugs will be fixed sooner. </span>
          </v-container>
        </v-scale-transition>
      </div>
    </v-scroll-y-transition>

    <!-- ========================================== -->

    <nav class="nav-controls">
      <v-scroll-x-reverse-transition>
        <v-btn
          v-if="stepper > 0 && stepper < 4"
          flat
          outline
          small
          :disabled="stepper===0"
          class="nav-controls__previous"
          @click="prevStep">
          back
        </v-btn>
      </v-scroll-x-reverse-transition>

      <div class="nav-controls__stepper">
        <div
          v-for="step in steps"
          :key="step"
          class="nav-controls__stepper-step-wrapper">
          <div
            class="nav-controls__stepper-step"
            :class="{
              'nav-controls__stepper-step--current': stepper===steps.indexOf(step),
              'nav-controls__stepper-step--complete': stepper > steps.indexOf(step)
            }">
            <v-icon
              class="nav-controls__stepper-icon">
              {{ step }}
            </v-icon>
          </div>
        </div>
      </div>

      <v-scroll-x-transition>
        <v-btn
          v-if="stepper > 0 && stepper < 4"
          flat
          outline
          small
          color="primary"
          :disabled="nextDisabled"
          class="nav-controls__next"
          @click="nextStep">
          next
        </v-btn>
      </v-scroll-x-transition>
    </nav>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

import api from '../../utils/api';

import CustomUpload from '../../components/patchCreator/CustomUpload.vue';
import CustomFormat from '../../components/patchCreator/CustomFormat.vue';

export default {
  name: 'SubmitDesign',
  components: {
    CustomUpload,
    CustomFormat,
  },
  data() {
    return {
      stepper: 0,
      steps: [
        'add_a_photo',
        'tune',
        'edit',
        'publish',
      ],
      sourceImageURL: '',
      outputImageURL: '',

      submitFormValid: false,
      tagLimit: 10,

      submitSuccess: false,
      submitFail: false,
      redirectCountdown: 0,

      formData: {
        name: '',
        alias: '',
        description: '',
        suggestedTags: [],
        agreeToTerms: false,
      },

    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user.currentUser;
    },
    ...mapState('patches', {
      tags: 'tags',
      imageDir: 'imageDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
    }),

    sortedTags() {
      return this.tags.map(x => x).sort();
    },
    tagCount() {
      return this.formData.suggestedTags.length;
    },

    nextDisabled() {
      if (this.stepper >= this.steps.length - 1) return true;
      if (this.stepper === 2 && !this.submitFormValid) return true;
      return false;
    },
  },

  watch: {
    tagCount(val) {
      if (val > this.tagLimit) {
        this.$nextTick(() => this.formData.suggestedTags.pop());
      }
    },
  },
  created() {
    this.$store.dispatch('patches/getTags');
    this.formData.alias = this.currentUser.alias;
  },
  methods: {
    prevStep() {
      this.stepper--;
    },
    nextStep() {
      if (this.stepper === 1) {
        this.createCanvas();
      }
      this.stepper++;
    },
    onUpload(event) {
      this.sourceImageURL = event;
      this.stepper++;
    },
    onCanvasOutput(event) {
      this.outputImageURL = event;
    },
    createCanvas() {
      this.$refs.customFormat.createCanvas();
    },

    validateAlias() {
      // check if alias is taken
      const formAlias = this.formData.alias;
      if (formAlias !== '' && formAlias !== this.currentUser.alias) {
        api
          .getData(`/customers/alias/${formAlias}`)
          .then(() => true)
          .catch(() => false);
      }
      return true;
    },
    updateAlias() {
      const accountDetails = {
        emailAddress: this.currentUser.email,
        alias: this.formData.alias,
      };
      const shippingAddress = Object.assign({}, this.currentUser.shippingAddress);
      this.$store
        .dispatch('user/updateUserProfile', {
          accountDetails,
          shippingAddress,
        })
        .then(() => {
          //
        });
    },

    submitDesign() {
      // begin processing
      this.nextStep();

      // update alias
      if (this.currentUser.alias !== this.formData.alias) {
        this.updateAlias();
      }

      // construct request data object
      const requestData = {
        uploadedImage: {
          base64: this.outputImageURL,
        },
        formData: {
          publicId: this.currentUser.publicId,
          name: this.formData.name,
          description: this.formData.description,
          suggestedTags: this.formData.suggestedTags,
        },
      };

      // POST data to server
      api
        .postData('/submissions/', requestData)
        .then((res) => {
          this.submitSuccess = true;
          this.redirectCountdown = 5;

          // COUNTDOWN
          const countdown = setInterval(() => {
            if (this.redirectCountdown > 1) {
              this.redirectCountdown--;
            } else {
              // REDIRECT
              clearInterval(countdown);
              this.$router.push(`/patches/submissions/${res.data.imageId}`);
            }
          }, 1000);
        })
        .catch(() => {
          this.submitFail = true;
        });
    },
  },
};

</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.submit-design
  position: relative
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: space-between
  padding: 0
  height: 99%
  max-width: none

.stepper
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  width: 100%
  height: 100%
  &__item
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    height: 100%

.nav-controls
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  width: 100%
  max-width: 28rem
  transition: $base-transition
  @media screen and (max-width: 48rem)
    max-width: 100%
  &__stepper
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    width: 100%
    &::before
      content: ''
      position: absolute
      z-index: 0
      height: 1px
      width: 100%
      background-color: $accent
    &-step-wrapper
      z-index: 1
      padding: 0 $base-padding
      background-color: $white
      border-radius: $base-border-radius
    &-step
      display: flex
      flex-flow: column nowrap
      align-items: center
      justify-content: center
      // margin-right: $base-padding
      // margin-left: $base-padding
      padding: $base-padding*.5
      color: $accent
      border-radius: $base-border-radius*2
      transition: $base-transition
      &--current
        color: $secondary
        background-color: $primary
      &--complete
        color: $white
        background-color: $accent
    &-icon
      color: inherit
  &__previous,
  &__next
    @media screen and (max-width: 28rem)
      min-width: 0
      padding: 0 $base-padding*.5

.edit-data
  display: flex
  flex-flow: row wrap
  align-content: flex-start
  height: 100%

  &__image-column
    position: relative
    display: flex
    flex-flow: column nowrap
    flex: 1 1 0
    align-items: center
    justify-content: flex-start

    padding-right: $base-padding*.5
    @media screen and (max-width: 48rem)
      flex-basis: 100%
      padding-right: 0

  &__form-column
    flex: 1 1 0
    padding-left: $base-padding*.5
    @media screen and (max-width: 48rem)
      flex-basis: 100%
      padding-left: 0

.custom-patch-image
  max-width: 100%

.patch-form
  text-align: center
  &__header
    font-size: 1.75rem
  &__terms
    font-size: $font-size--small

.tag-select
  &__selected-tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    background-color: $accent--light
    cursor: pointer
    &::before
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      z-index: 1
      background-color: $black
      opacity: 0
      transition: $base-transition
      cursor: pointer
    &:hover::before
      opacity: .12
  &__selected-tag-remove
    margin-left: $base-padding


.submit-preview
  display: flex
  flex-flow: row wrap
  align-content: flex-start
  height: 100%

.preview
  position: relative
  display: flex
  flex-flow: column nowrap
  flex: 1 1 0
  align-items: stretch
  justify-content: flex-start

  padding: $base-padding
  padding-bottom: 0
  box-shadow: $base-box-shadow
  @media screen and (max-width: 36rem)
    flex-basis: 100%

  &__name
    font-size: 1.75rem
  &__creator-row
    display: flex
    flex-wrap: wrap
    justify-content: space-between
    font-size: $font-size--small
    color: $accent--dark
  &__description
    padding-top: $base-padding
    padding-bottom: $base-padding
  &__tags
    display: flex
    flex-flow: row wrap
    padding: $base-padding 0 0 0
  &__tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    margin-bottom: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    background-color: $accent--light
  &__no-tags
    padding-bottom: $base-padding
    font-size: $font-size--small
    font-style: italic
    text-transform: uppercase
    color: $accent--dark

.submit-info
  flex: 1 1 0
  padding-left: $base-padding
  @media screen and (max-width: 26rem)
    flex-basis: 100%
    padding-left: 0
    padding-top: $base-padding*2
  &__header
    font-size: 1.75rem

.submit-button
  display: flex
  flex: 1 1 100%
  margin: 0
  &__wrapper
    display: flex
    align-items: center
    justify-content: space-between
    padding-top: $base-padding
    padding-bottom: $base-padding

.processing
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  padding: $base-padding*2
  color: $primary
  border-radius: $base-border-radius
  transition: $base-transition
  &__header
    font-size: 1.5rem
    font-weight: $base-header-font-weight
    text-align: center
  &__loader
    margin: $base-padding*4
  &__icon
    color: inherit
    font-size: 8rem
  &__redirect-countdown
    padding: $base-padding $base-padding*2
    text-transform: capitalize
    color: $secondary
    background-color: $primary
    border-radius: $base-border-radius

</style>
